import { z } from 'zod';
import {
  NORMALIZED_DIFFICULTIES,
  PLATFORMS,
  SESSION_KINDS,
  TOPIC_STATUSES,
  type NormalizedDifficulty,
  type Platform,
  type SessionKind,
  type TopicStatus,
} from './enums';

/** Turns a const-object enum into a Zod enum that keeps its literal union type. */
const enumOf = <T extends string>(values: readonly T[]) =>
  z.enum(values as unknown as [T, ...T[]]);

export const platformSchema = enumOf<Platform>(PLATFORMS);
export const difficultySchema = enumOf<NormalizedDifficulty>(NORMALIZED_DIFFICULTIES);
export const topicStatusSchema = enumOf<TopicStatus>(TOPIC_STATUSES);
export const sessionKindSchema = enumOf<SessionKind>(SESSION_KINDS);

const nonEmpty = (max = 500) => z.string().trim().min(1).max(max);
const optionalText = (max = 20_000) =>
  z
    .string()
    .max(max)
    .transform((v) => (v.trim().length === 0 ? null : v))
    .nullable()
    .optional();

export const localDaySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'expected a YYYY-MM-DD local day');

export const isoDateTimeSchema = z
  .string()
  .datetime({ offset: true })
  .or(z.string().datetime());

export const uuidSchema = z.string().uuid();
export const idParamSchema = z.object({ id: z.string().min(1) });

/* ----------------------------------- auth --------------------------------- */

export const loginSchema = z.object({
  username: z.string().trim().min(1).max(80),
  password: z.string().min(1).max(200),
});
export type LoginInput = z.infer<typeof loginSchema>;

export interface LoginResult {
  /** The API token to send as `x-api-token` on every later request. */
  token: string;
  displayName: string;
}

/* --------------------------------- profile -------------------------------- */

export const updateProfileSchema = z.object({
  displayName: nonEmpty(120).optional(),
  timezone: nonEmpty(80).optional(),
  dailyGoalMinutes: z.number().int().min(0).max(1440).optional(),
  dailyGoalProblems: z.number().int().min(0).max(100).optional(),
  theme: z.enum(['dark', 'light']).optional(),
});
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

/* ---------------------------------- topics -------------------------------- */

export const updateTopicSchema = z.object({
  status: topicStatusSchema.optional(),
  confidence: z.number().int().min(1).max(5).optional(),
  notes: z.string().max(100_000).optional(),
});
export type UpdateTopicInput = z.infer<typeof updateTopicSchema>;

export const createResourceSchema = z.object({
  title: nonEmpty(200),
  url: z.string().url().max(2000),
});
export type CreateResourceInput = z.infer<typeof createResourceSchema>;

/* --------------------------------- problems ------------------------------- */

export const createProblemSchema = z.object({
  name: nonEmpty(300),
  platform: platformSchema,
  url: z.string().url().max(2000).nullable().optional(),
  platformDifficulty: z.string().trim().max(60).default(''),
  normalizedDifficulty: difficultySchema,
  topicIds: z.array(z.string().min(1)).max(20).default([]),
  /** Optional first solution recorded together with the problem. */
  solution: z
    .object({
      link: z.string().url().max(2000).nullable().optional(),
      explanation: z.string().max(100_000).default(''),
      code: optionalText(200_000),
      language: z.string().max(40).nullable().optional(),
      solvedAt: isoDateTimeSchema.optional(),
      minutesSpent: z.number().int().min(0).max(24 * 60),
      newConcept: optionalText(),
      mistakeMade: optionalText(),
      optimizationIdea: optionalText(),
    })
    .optional(),
});
export type CreateProblemInput = z.infer<typeof createProblemSchema>;

export const updateProblemSchema = createProblemSchema
  .omit({ solution: true })
  .partial();
export type UpdateProblemInput = z.infer<typeof updateProblemSchema>;

export const problemQuerySchema = z.object({
  q: z.string().max(200).optional(),
  platform: platformSchema.optional(),
  difficulty: difficultySchema.optional(),
  topicId: z.string().min(1).optional(),
  take: z.coerce.number().int().min(1).max(500).default(100),
  skip: z.coerce.number().int().min(0).default(0),
});
export type ProblemQuery = z.infer<typeof problemQuerySchema>;

/* -------------------------------- solutions ------------------------------- */

export const createSolutionSchema = z.object({
  link: z.string().url().max(2000).nullable().optional(),
  explanation: z.string().max(100_000).default(''),
  code: optionalText(200_000),
  language: z.string().max(40).nullable().optional(),
  solvedAt: isoDateTimeSchema.optional(),
  minutesSpent: z.number().int().min(0).max(24 * 60),
  newConcept: optionalText(),
  mistakeMade: optionalText(),
  optimizationIdea: optionalText(),
});
export type CreateSolutionInput = z.infer<typeof createSolutionSchema>;

export const updateSolutionSchema = createSolutionSchema.partial();
export type UpdateSolutionInput = z.infer<typeof updateSolutionSchema>;

/* ------------------------------- daily tasks ------------------------------ */

export const createDailyTaskSchema = z.object({
  date: localDaySchema,
  title: nonEmpty(300),
  topicId: z.string().min(1).nullable().optional(),
  estimatedMinutes: z.number().int().min(0).max(1440).nullable().optional(),
  actualMinutes: z.number().int().min(0).max(1440).nullable().optional(),
  done: z.boolean().default(false),
});
export type CreateDailyTaskInput = z.infer<typeof createDailyTaskSchema>;

export const updateDailyTaskSchema = createDailyTaskSchema.partial().extend({
  sortOrder: z.number().int().min(0).optional(),
});
export type UpdateDailyTaskInput = z.infer<typeof updateDailyTaskSchema>;

/* ---------------------------- learning sessions --------------------------- */

export const createSessionSchema = z.object({
  date: isoDateTimeSchema.optional(),
  minutes: z.number().int().min(1).max(24 * 60),
  topicId: z.string().min(1).nullable().optional(),
  kind: sessionKindSchema,
  note: optionalText(2000),
});
export type CreateSessionInput = z.infer<typeof createSessionSchema>;

export const updateSessionSchema = createSessionSchema.partial();
export type UpdateSessionInput = z.infer<typeof updateSessionSchema>;

export const sessionQuerySchema = z.object({
  from: localDaySchema.optional(),
  to: localDaySchema.optional(),
  topicId: z.string().min(1).optional(),
  take: z.coerce.number().int().min(1).max(1000).default(200),
});
export type SessionQuery = z.infer<typeof sessionQuerySchema>;

/* ----------------------------------- notes -------------------------------- */

export const createNoteSchema = z.object({
  title: nonEmpty(300),
  category: z.string().trim().max(100).default('General'),
  content: z.string().max(500_000).default(''),
  tags: z.array(z.string().trim().min(1).max(40)).max(30).default([]),
  topicId: z.string().min(1).nullable().optional(),
  problemId: z.string().min(1).nullable().optional(),
});
export type CreateNoteInput = z.infer<typeof createNoteSchema>;

export const updateNoteSchema = createNoteSchema.partial();
export type UpdateNoteInput = z.infer<typeof updateNoteSchema>;

export const noteQuerySchema = z.object({
  q: z.string().max(200).optional(),
  tag: z.string().max(40).optional(),
  category: z.string().max(100).optional(),
  topicId: z.string().min(1).optional(),
  take: z.coerce.number().int().min(1).max(500).default(100),
});
export type NoteQuery = z.infer<typeof noteQuerySchema>;

/* --------------------------------- reports -------------------------------- */

export const weeklyPeriodSchema = z.object({
  period: z
    .string()
    .regex(/^\d{4}-W\d{2}$/, 'expected an ISO week like 2026-W31')
    .optional(),
  refresh: z.coerce.boolean().default(false),
});

export const monthlyPeriodSchema = z.object({
  period: z
    .string()
    .regex(/^\d{4}-\d{2}$/, 'expected a month like 2026-07')
    .optional(),
  refresh: z.coerce.boolean().default(false),
});

export const heatmapQuerySchema = z.object({
  days: z.coerce.number().int().min(7).max(400).default(365),
});

export const recommendationQuerySchema = z.object({
  count: z.coerce.number().int().min(1).max(20).default(5),
});
